// ── AttitudeCanvas.js ──────────────────────────────────────────────────────────
// Animated canvas: Cold Gas MIB bang-bang attitude control vs Reaction Wheel PID
// Both start at 8° pointing error and drive toward 0° (nadir target).
//
// Physics based on same J_mib / F_reset / isp parameters as CyclePage DEF.
// Cold gas: bang-bang switching curve controller, minimum impulse bit pulses.
// Reaction wheel: PID (KP, KD, KI) torque → wheel angular velocity saturation.

import { useState, useRef, useEffect, useCallback } from "react";
import { useTheme, mono } from "./appHelpers";

// ── Tunable constants ──────────────────────────────────────────────────────────
const TAU_DIST   = 3e-6;                      // N·m — constant disturbance torque
const SIM_SPEED  = 10;                        // × real time
const INIT_ERR   = 8 * Math.PI / 180;        // 8° initial pointing error (rad)
const KP_RW      = 0.025;
const KD_RW      = 0.09;
const KI_RW      = 0.001;
const G0         = 9.81;                      // m/s²
const RAD2ARCSEC = 206265;
const HIST_SECS  = 120;                       // sim-seconds of history to plot
const MFONT      = "'JetBrains Mono', monospace";

// Canvas dimensions
const W = 820;
const H = 370;

// Layout
const PLOT_X     = 225;   // x where time-history plots start
const PLOT_W     = W - PLOT_X - 10;
const TOP_Y1     = 25;
const TOP_Y2     = 162;
const BOT_Y1     = 185;
const BOT_Y2     = 320;
const STATS_Y    = 335;

// ── Rounded-rect helper ────────────────────────────────────────────────────────
function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function AttitudeCanvas({ params }) {
  const [running, setRunning] = useState(false);

  const canvasRef  = useRef(null);
  const stateRef   = useRef(null);
  const histRef    = useRef([]);
  const rafRef     = useRef(null);
  const lastTsRef  = useRef(null);
  const themeRef   = useRef(null);

  const theme = useTheme();
  themeRef.current = theme;

  // ── Derived physics from params ──────────────────────────────────────────────
  const paramsRef = useRef(params);
  useEffect(() => { paramsRef.current = params; }, [params]);

  function computePhysics(p) {
    const M        = p.m_hull + p.m_emms;
    const L_w      = p.L_hull * 0.5;
    const I_sat    = (M / 12) * (p.L_hull * p.L_hull + L_w * L_w);
    const arm      = p.L_hull * 0.45;
    const H_mib    = p.J_mib * arm;
    const dOmega   = H_mib / I_sat;
    const dt_burn  = p.J_mib / p.F_reset;
    const alpha_max = dOmega / dt_burn;
    const alpha_dist = TAU_DIST / I_sat;
    return { M, L_w, I_sat, arm, H_mib, dOmega, dt_burn, alpha_max, alpha_dist };
  }

  // ── Initial state factory ────────────────────────────────────────────────────
  function makeState() {
    return {
      // Cold gas
      cg_theta:    INIT_ERR,
      cg_omega:    0,
      cg_prop:     0,
      cg_pulses:   0,
      cg_cooldown: 0,
      cg_firing:   false,
      cg_fire_dir: 0,
      // Reaction wheel
      rw_theta:    INIT_ERR,
      rw_omega:    0,
      rw_integral: 0,
      rw_wheel_v:  0,
      rw_torque:   0,
      rw_power:    0,
      tAbs:        0,
    };
  }

  function reset() {
    stateRef.current = makeState();
    histRef.current  = [];
    lastTsRef.current = null;
  }

  // Initialise on mount
  useEffect(() => { reset(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Simulation advance ───────────────────────────────────────────────────────
  function advance(dtWall) {
    const p  = paramsRef.current;
    if (!p) return;
    const ph = computePhysics(p);
    const { I_sat, dOmega, dt_burn, alpha_max, alpha_dist } = ph;

    const dtSim    = Math.min(dtWall * SIM_SPEED, 3);
    const N        = 40;
    const dt       = dtSim / N;
    const deadband = dOmega * dt_burn * 0.05;

    let s = { ...stateRef.current };

    for (let i = 0; i < N; i++) {
      // ── Cold gas controller ──────────────────────────────────────────────────
      s.cg_cooldown = Math.max(0, s.cg_cooldown - dt);

      const phasePos = s.cg_theta + Math.sign(s.cg_omega) * (s.cg_omega * s.cg_omega) / (2 * alpha_max);

      s.cg_firing   = false;
      s.cg_fire_dir = 0;

      if (s.cg_cooldown <= 0) {
        if (phasePos > deadband) {
          // fire negative pulse (CW — reduces positive theta)
          s.cg_omega    -= dOmega;
          s.cg_prop     += p.J_mib / (p.isp * G0);
          s.cg_pulses   += 1;
          s.cg_cooldown  = dt_burn;
          s.cg_firing    = true;
          s.cg_fire_dir  = -1;
        } else if (phasePos < -deadband) {
          // fire positive pulse (CCW — increases theta toward zero from negative)
          s.cg_omega    += dOmega;
          s.cg_prop     += p.J_mib / (p.isp * G0);
          s.cg_pulses   += 1;
          s.cg_cooldown  = dt_burn;
          s.cg_firing    = true;
          s.cg_fire_dir  = +1;
        }
      }

      // disturbance on cg
      s.cg_omega += alpha_dist * dt;
      s.cg_omega += 0; // drag / no friction — purely disturbance-driven
      s.cg_theta += s.cg_omega * dt;

      // ── Reaction wheel PID ───────────────────────────────────────────────────
      s.rw_integral = Math.max(-0.05, Math.min(0.05, s.rw_integral + s.rw_theta * dt));

      const tau = -KP_RW * s.rw_theta - KD_RW * s.rw_omega - KI_RW * s.rw_integral;

      s.rw_omega   += (tau + TAU_DIST) / I_sat * dt;
      s.rw_theta   += s.rw_omega * dt;
      s.rw_wheel_v -= tau / I_sat * dt;   // reaction: wheel spins opposite
      s.rw_torque   = tau;
      s.rw_power    = Math.abs(tau * s.rw_wheel_v) * 1000;  // mW

      s.tAbs += dt;
    }

    stateRef.current = s;

    // record history
    const hist = histRef.current;
    hist.push({
      t:            s.tAbs,
      cg_err_arcsec: s.cg_theta * RAD2ARCSEC,
      rw_err_arcsec: s.rw_theta * RAD2ARCSEC,
    });
    // trim to HIST_SECS window
    const tCutoff = s.tAbs - HIST_SECS;
    while (hist.length > 2 && hist[0].t < tCutoff) hist.shift();
  }

  // ── Draw ─────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const Cx  = themeRef.current ?? {};

    const bg     = Cx.bg     ?? "#0a0f1a";
    const panel  = Cx.panel  ?? "#111827";
    const border = Cx.border ?? "#1e2d3d";
    const muted  = Cx.muted  ?? "#4b5563";
    const text   = Cx.text   ?? "#e2e8f0";
    const eqBg   = Cx.eqBg   ?? "#0d1117";

    const s    = stateRef.current;
    const hist = histRef.current;
    const p    = paramsRef.current;

    if (!s || !p) return;

    const ph = computePhysics(p);
    const { I_sat, dOmega, dt_burn, arm } = ph;
    const deadband = dOmega * dt_burn * 0.05;

    // ── Background ────────────────────────────────────────────────────────────
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // ── Vertical divider ─────────────────────────────────────────────────────
    ctx.strokeStyle = border;
    ctx.lineWidth   = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(210, 0); ctx.lineTo(210, H - 35);
    ctx.stroke();

    // ── Horizontal divider between CG and RW sections ────────────────────────
    ctx.beginPath();
    ctx.moveTo(0, 170); ctx.lineTo(W, 170);
    ctx.stroke();

    // ── Stats bar divider ─────────────────────────────────────────────────────
    ctx.beginPath();
    ctx.moveTo(0, STATS_Y); ctx.lineTo(W, STATS_Y);
    ctx.stroke();

    // ═════════════════════════════════════════════════════════════════════════
    // SATELLITE VIEWS — left panel
    // ═════════════════════════════════════════════════════════════════════════

    // ── CG section ───────────────────────────────────────────────────────────
    drawSatSection(ctx, {
      cx: 105, cy: 95,
      theta: s.cg_theta,
      color: "#a855f7",
      isFiring: s.cg_firing,
      fireDir: s.cg_fire_dir,
      wheelV: 0,
      isRW: false,
      Cx: { bg, panel, border, muted, text, eqBg },
      titleLines: [
        "COLD GAS MIB",
        `J=${(p.J_mib * 1000).toFixed(1)}mNs arm=${(arm * 1000).toFixed(0)}mm Δω=${(dOmega * 1000).toFixed(2)}mrad/s`,
        `pulses: ${s.cg_pulses}  prop: ${(s.cg_prop * 1e9).toFixed(1)} ng`,
      ],
    });

    // ── RW section ───────────────────────────────────────────────────────────
    const maxWheelV = 500;
    drawSatSection(ctx, {
      cx: 105, cy: 260,
      theta: s.rw_theta,
      color: "#0e9cc0",
      isFiring: false,
      fireDir: 0,
      wheelV: s.rw_wheel_v,
      isRW: true,
      Cx: { bg, panel, border, muted, text, eqBg },
      titleLines: [
        "REACTION WHEEL (PD+I)",
        `KP=${KP_RW}  KD=${KD_RW}  KI=${KI_RW}`,
        `wheel: ${(s.rw_wheel_v * 60 / (2 * Math.PI)).toFixed(0)} rpm  power: ${s.rw_power.toFixed(2)} mW`,
      ],
      maxWheelV,
    });

    // ═════════════════════════════════════════════════════════════════════════
    // TIME-HISTORY PLOTS — right panel
    // ═════════════════════════════════════════════════════════════════════════

    // ── Top plot: CG error in degrees ────────────────────────────────────────
    drawTimePlot(ctx, {
      x: PLOT_X, y: TOP_Y1, w: PLOT_W, h: TOP_Y2 - TOP_Y1,
      hist,
      getVal: d => d.cg_err_arcsec / RAD2ARCSEC * 180,  // arcsec → degrees
      yRange: 12,
      color: "#a855f7",
      title: "COLD GAS — ERROR",
      unit: "deg",
      deadbandDeg: deadband * 180 / Math.PI,
      showDeadband: true,
      curVal: s.cg_theta * 180 / Math.PI,
      Cx: { bg, panel, border, muted, text, eqBg },
      eqBg,
    });

    // ── Bottom plot: RW error in arcseconds ──────────────────────────────────
    drawTimePlot(ctx, {
      x: PLOT_X, y: BOT_Y1, w: PLOT_W, h: BOT_Y2 - BOT_Y1,
      hist,
      getVal: d => d.rw_err_arcsec,
      yRange: 200,
      color: "#0e9cc0",
      title: "REACTION WHEEL — ERROR",
      unit: "arcsec",
      showDeadband: false,
      curVal: s.rw_theta * RAD2ARCSEC,
      Cx: { bg, panel, border, muted, text, eqBg },
      eqBg,
    });

    // ═════════════════════════════════════════════════════════════════════════
    // STATS BAR
    // ═════════════════════════════════════════════════════════════════════════
    drawStats(ctx, s, hist, { bg, panel, border, muted, text, eqBg });

  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── RAF loop ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    function loop(ts) {
      rafRef.current = requestAnimationFrame(loop);
      if (running) {
        if (lastTsRef.current !== null) {
          advance((ts - lastTsRef.current) / 1000);
        }
        lastTsRef.current = ts;
      } else {
        lastTsRef.current = null;
      }
      draw();
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [running, draw]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── JSX ───────────────────────────────────────────────────────────────────────
  const Cx = theme ?? {};
  const C  = {
    muted:  Cx.muted  ?? "#4b5563",
    border: Cx.border ?? "#1e2d3d",
  };

  // Compute I_sat for display
  let I_sat_display = 0;
  if (params) {
    const ph = computePhysics(params);
    I_sat_display = ph.I_sat;
  }

  const btnStyle = {
    padding: "5px 14px",
    ...mono,
    fontSize: 9,
    letterSpacing: 1.5,
    cursor: "pointer",
    background: "transparent",
    border: `1px solid ${C.border}`,
    color: C.muted,
    borderRadius: 4,
    textTransform: "uppercase",
  };

  return (
    <div>
      {/* Controls row */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => setRunning(r => !r)} style={btnStyle}>
          {running ? "■ STOP" : "▶ RUN"}
        </button>
        <button onClick={() => { reset(); }} style={btnStyle}>
          ⟳ RESET
        </button>
        <span style={{ ...mono, fontSize: 8, color: C.muted }}>
          SIM {SIM_SPEED}× · disturbance τ = {(TAU_DIST * 1e6).toFixed(0)} μN·m · I_sat = {I_sat_display.toFixed(3)} kg·m²
        </span>
      </div>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{ width: "100%", height: "auto", display: "block" }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DRAWING HELPERS (module-level functions, no hooks)
// ═══════════════════════════════════════════════════════════════════════════════

function drawSatSection(ctx, { cx, cy, theta, color, isFiring, fireDir, wheelV, isRW, Cx, titleLines, maxWheelV }) {
  const { muted, text, border } = Cx;

  // ── Section title lines (above sat, left-aligned) ───────────────────────────
  const titleX = 8;
  const titleTopY = cy - 65;

  ctx.font = `bold 9px ${MFONT}`;
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  ctx.fillText(titleLines[0], titleX, titleTopY);

  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.fillText(titleLines[1], titleX, titleTopY + 12);

  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = text;
  ctx.fillText(titleLines[2], titleX, titleTopY + 22);

  // ── Draw the satellite body ──────────────────────────────────────────────────
  drawSatView(ctx, cx, cy, theta, color, isFiring, fireDir, wheelV, isRW, Cx, maxWheelV);
}

function drawSatView(ctx, cx, cy, theta, color, isFiring, fireDir, wheelV, isRW, Cx, maxWheelV) {
  const { muted, border } = Cx;
  const bodyW = 64;
  const bodyH = 30;
  const panelW = 26;
  const panelH = 8;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(theta);

  // ── Satellite body ───────────────────────────────────────────────────────────
  const alpha = Math.min(1, Math.abs(theta) / (8 * Math.PI / 180));
  ctx.fillStyle = blendHex(color, "#1e2d3d", 0.25 + 0.15 * alpha);
  ctx.strokeStyle = color + "99";
  ctx.lineWidth = 1.5;
  rr(ctx, -bodyW / 2, -bodyH / 2, bodyW, bodyH, 5);
  ctx.fill();
  ctx.stroke();

  // ── Solar panels ─────────────────────────────────────────────────────────────
  ctx.fillStyle = "#1e3a5f";
  ctx.strokeStyle = "#0e9cc055";
  ctx.lineWidth = 0.8;
  // left panel
  ctx.beginPath();
  ctx.rect(-bodyW / 2 - panelW - 4, -panelH / 2, panelW, panelH);
  ctx.fill(); ctx.stroke();
  // right panel
  ctx.beginPath();
  ctx.rect(bodyW / 2 + 4, -panelH / 2, panelW, panelH);
  ctx.fill(); ctx.stroke();

  // ── Pointing arrow (forward = +x direction in body frame) ────────────────────
  ctx.strokeStyle = color;
  ctx.lineWidth   = 1.5;
  ctx.fillStyle   = color;
  const arrowLen = bodyW / 2 + 18;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(arrowLen, 0);
  ctx.stroke();
  // arrowhead
  ctx.beginPath();
  ctx.moveTo(arrowLen, 0);
  ctx.lineTo(arrowLen - 7, -3.5);
  ctx.lineTo(arrowLen - 7, 3.5);
  ctx.closePath();
  ctx.fill();

  // ── Reaction wheel indicator (inside body) ───────────────────────────────────
  if (isRW && maxWheelV) {
    const wr = 9;
    const satFrac = Math.min(1, Math.abs(wheelV) / maxWheelV);
    const baseAlpha = 0.15;

    ctx.beginPath();
    ctx.arc(0, 0, wr, 0, 2 * Math.PI);
    ctx.fillStyle = "#0e9cc0" + hex2(Math.round(baseAlpha * 255));
    ctx.fill();
    ctx.strokeStyle = "#0e9cc066";
    ctx.lineWidth = 1;
    ctx.stroke();

    if (satFrac > 0.01) {
      ctx.beginPath();
      ctx.moveTo(0, 0);
      const sweep = satFrac * 2 * Math.PI;
      ctx.arc(0, 0, wr, -Math.PI / 2, -Math.PI / 2 + sweep);
      ctx.closePath();
      const intensity = Math.round((0.4 + 0.6 * satFrac) * 255);
      ctx.fillStyle = "#0e9cc0" + hex2(intensity);
      ctx.fill();
    }
  }

  // ── CG thruster firing glows and exhaust ─────────────────────────────────────
  if (isFiring && fireDir !== 0) {
    // Corners: body frame coords
    // fireDir=-1 (CCW): top-right (bodyW/2, -bodyH/2) and bottom-left (-bodyW/2, bodyH/2)
    // fireDir=+1 (CW):  top-left (-bodyW/2, -bodyH/2) and bottom-right (bodyW/2, bodyH/2)
    const corners = fireDir === -1
      ? [[bodyW / 2, -bodyH / 2], [-bodyW / 2, bodyH / 2]]
      : [[-bodyW / 2, -bodyH / 2], [bodyW / 2, bodyH / 2]];

    corners.forEach(([px, py]) => {
      // glow dot
      const grd = ctx.createRadialGradient(px, py, 0, px, py, 10);
      grd.addColorStop(0, "#ffdd8888");
      grd.addColorStop(1, "transparent");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(px, py, 10, 0, 2 * Math.PI);
      ctx.fill();

      // exhaust line perpendicular to body edge, outward
      const exDir = py < 0 ? -1 : 1;
      ctx.strokeStyle = "#ffaa4499";
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px, py + exDir * 14);
      ctx.stroke();
    });
  }

  ctx.restore();

  // ── Target line (dashed horizontal, theta=0 direction) ───────────────────────
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = Cx.border + "99";
  ctx.lineWidth   = 0.8;
  ctx.beginPath();
  ctx.moveTo(cx - 90, cy);
  ctx.lineTo(cx + 90, cy);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // ── Error arc ────────────────────────────────────────────────────────────────
  if (Math.abs(theta) > 0.001) {
    ctx.save();
    ctx.translate(cx, cy);
    const arcR = bodyW / 2 + 10;
    ctx.strokeStyle = color + "66";
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.arc(0, 0, arcR, 0, theta, theta < 0);
    ctx.stroke();
    ctx.restore();
  }

  // ── Error label below satellite ──────────────────────────────────────────────
  const labelY = cy + bodyH / 2 + 28;
  const errDeg = theta * 180 / Math.PI;
  let errStr;
  if (isRW) {
    const errAS = theta * RAD2ARCSEC;
    errStr = `${errAS.toFixed(1)}"`;
  } else {
    errStr = `${errDeg.toFixed(2)}°`;
  }
  ctx.font      = `bold 9px ${MFONT}`;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.fillText(`err: ${errStr}`, cx, labelY);
}

function drawTimePlot(ctx, { x, y, w, h, hist, getVal, yRange, color, title, unit, deadbandDeg, showDeadband, curVal, Cx, eqBg }) {
  const { muted, border, text } = Cx;

  // Background
  ctx.fillStyle = eqBg;
  rr(ctx, x, y, w, h, 4);
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth   = 0.5;
  rr(ctx, x, y, w, h, 4);
  ctx.stroke();

  const px = x + 42;
  const pw = w - 52;
  const py = y + 6;
  const ph = h - 14;
  const midY = py + ph / 2;

  // ── Grid lines ────────────────────────────────────────────────────────────────
  ctx.strokeStyle = border + "88";
  ctx.lineWidth   = 0.5;
  ctx.setLineDash([2, 3]);
  [0.25, 0.5, 0.75].forEach(frac => {
    const gy = py + ph * frac;
    ctx.beginPath();
    ctx.moveTo(px, gy); ctx.lineTo(px + pw, gy);
    ctx.stroke();
    const gy2 = py + ph * (1 - frac);
    ctx.beginPath();
    ctx.moveTo(px, gy2); ctx.lineTo(px + pw, gy2);
    ctx.stroke();
  });
  // Zero line
  ctx.strokeStyle = border;
  ctx.lineWidth   = 0.8;
  ctx.beginPath();
  ctx.moveTo(px, midY); ctx.lineTo(px + pw, midY);
  ctx.stroke();
  ctx.setLineDash([]);

  // ── Deadband lines ────────────────────────────────────────────────────────────
  if (showDeadband && deadbandDeg != null) {
    const dbFrac = deadbandDeg / yRange;
    if (dbFrac < 0.5) {
      ctx.strokeStyle = "#a855f744";
      ctx.lineWidth   = 0.7;
      ctx.setLineDash([3, 3]);
      const dbYp = midY - dbFrac * (ph / 2) * 2; // map +deadband
      const dbYn = midY + dbFrac * (ph / 2) * 2; // map -deadband
      ctx.beginPath(); ctx.moveTo(px, dbYp); ctx.lineTo(px + pw, dbYp); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px, dbYn); ctx.lineTo(px + pw, dbYn); ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  // ── Y-axis labels ─────────────────────────────────────────────────────────────
  ctx.font      = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.textAlign = "right";
  ctx.fillText(`+${yRange}`, px - 3, py + 5);
  ctx.fillText(`0`, px - 3, midY + 3);
  ctx.fillText(`-${yRange}`, px - 3, py + ph + 2);

  // ── Y-axis unit label (rotated) ───────────────────────────────────────────────
  ctx.save();
  ctx.translate(x + 8, midY);
  ctx.rotate(-Math.PI / 2);
  ctx.font      = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.textAlign = "center";
  ctx.fillText(unit, 0, 0);
  ctx.restore();

  // ── Title ─────────────────────────────────────────────────────────────────────
  ctx.font      = `bold 7px ${MFONT}`;
  ctx.fillStyle = color + "bb";
  ctx.textAlign = "left";
  ctx.fillText(title, px + 4, py + 10);

  // ── Data line ─────────────────────────────────────────────────────────────────
  if (hist.length > 1) {
    const tNow  = hist[hist.length - 1].t;
    const tMin  = tNow - HIST_SECS;

    ctx.strokeStyle = color;
    ctx.lineWidth   = 1.2;
    ctx.beginPath();
    let first = true;
    for (let i = 0; i < hist.length; i++) {
      const d  = hist[i];
      const tx = px + ((d.t - tMin) / HIST_SECS) * pw;
      const v  = getVal(d);
      const ty = midY - (v / yRange) * (ph / 2);
      const tyClamped = Math.max(py, Math.min(py + ph, ty));
      if (first) { ctx.moveTo(tx, tyClamped); first = false; }
      else ctx.lineTo(tx, tyClamped);
    }
    ctx.stroke();
  }

  // ── Current value label ───────────────────────────────────────────────────────
  const cvStr = `${curVal.toFixed(unit === "deg" ? 3 : 1)} ${unit}`;
  ctx.font      = `8px ${MFONT}`;
  ctx.fillStyle = color;
  ctx.textAlign = "right";
  ctx.fillText(cvStr, x + w - 4, y + h - 6);

  // ── X-axis time labels ────────────────────────────────────────────────────────
  if (hist.length > 1) {
    const tNow = hist[hist.length - 1].t;
    const tMin = tNow - HIST_SECS;
    ctx.font      = `6px ${MFONT}`;
    ctx.fillStyle = muted;
    ctx.textAlign = "center";
    [0, 0.25, 0.5, 0.75, 1].forEach(frac => {
      const tx = px + frac * pw;
      const tv = tMin + frac * HIST_SECS;
      ctx.fillText(`${tv.toFixed(0)}s`, tx, py + ph + 10);
    });
  }
}

function drawStats(ctx, s, hist, Cx) {
  const { muted, text, border, eqBg } = Cx;
  const y = STATS_Y + 2;

  ctx.font      = `7px ${MFONT}`;
  ctx.textAlign = "left";

  // compute RMS of last 50 samples
  const last50 = hist.slice(-50);
  let cgRms = 0, rwRms = 0;
  if (last50.length > 0) {
    const cgSS = last50.reduce((a, d) => a + d.cg_err_arcsec * d.cg_err_arcsec, 0);
    const rwSS = last50.reduce((a, d) => a + d.rw_err_arcsec * d.rw_err_arcsec, 0);
    cgRms = Math.sqrt(cgSS / last50.length);
    rwRms = Math.sqrt(rwSS / last50.length);
  }

  const cgRmsStr = Math.abs(cgRms) > RAD2ARCSEC * Math.PI / 180
    ? `${(cgRms / RAD2ARCSEC * 180 / Math.PI).toFixed(2)}°`
    : `${cgRms.toFixed(0)}"`;

  const rwRmsStr = `${rwRms.toFixed(1)}"`;

  const ratio = rwRms > 0 && cgRms > 0 ? (cgRms / rwRms).toFixed(0) : "—";

  const propNg = s.cg_prop * 1e9;
  const wheelRpm = (s.rw_wheel_v * 60 / (2 * Math.PI)).toFixed(0);

  // Left cluster — CG purple
  let lx = 8;
  ctx.fillStyle = "#a855f7";
  ctx.font = `bold 7px ${MFONT}`;
  ctx.fillText("CG ACCURACY", lx, y + 10);
  ctx.font = `9px ${MFONT}`;
  ctx.fillText(cgRmsStr + " rms", lx, y + 22);

  lx += 120;
  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.fillText("PULSES", lx, y + 10);
  ctx.font = `9px ${MFONT}`;
  ctx.fillStyle = "#a855f7";
  ctx.fillText(`${s.cg_pulses}`, lx, y + 22);

  lx += 70;
  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.fillText("PROP", lx, y + 10);
  ctx.font = `9px ${MFONT}`;
  ctx.fillStyle = "#a855f7";
  ctx.fillText(`${propNg.toFixed(1)} ng`, lx, y + 22);

  // Middle — comparison
  const midX = W / 2 - 60;
  ctx.font      = `bold 8px ${MFONT}`;
  ctx.fillStyle = text;
  ctx.textAlign = "center";
  ctx.fillText(`RW is ${ratio}× more precise`, midX + 60, y + 16);

  // Right cluster — RW cyan
  const rx = W - 240;
  ctx.textAlign = "left";
  ctx.font = `bold 7px ${MFONT}`;
  ctx.fillStyle = "#0e9cc0";
  ctx.fillText("RW ACCURACY", rx, y + 10);
  ctx.font = `9px ${MFONT}`;
  ctx.fillText(rwRmsStr + " rms", rx, y + 22);

  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.fillText("WHEEL", rx + 110, y + 10);
  ctx.font = `9px ${MFONT}`;
  ctx.fillStyle = "#0e9cc0";
  ctx.fillText(`${wheelRpm} rpm`, rx + 110, y + 22);

  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = muted;
  ctx.fillText("POWER", rx + 175, y + 10);
  ctx.font = `9px ${MFONT}`;
  ctx.fillStyle = "#0e9cc0";
  ctx.fillText(`${s.rw_power.toFixed(2)} mW`, rx + 175, y + 22);
}

// ── Colour utilities ──────────────────────────────────────────────────────────
function hex2(n) {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
}

function blendHex(colorHex, baseHex, t) {
  // t=0 → colorHex, t=1 → baseHex
  const parse = h => {
    const s = h.replace("#", "");
    return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
  };
  const [r1, g1, b1] = parse(colorHex.slice(0, 7));
  const [r2, g2, b2] = parse(baseHex.slice(0, 7));
  const r = Math.round(r1 * (1 - t) + r2 * t);
  const g = Math.round(g1 * (1 - t) + g2 * t);
  const b = Math.round(b1 * (1 - t) + b2 * t);
  return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
}
